/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication. Redirects to login if user is not authenticated.
 * 
 * Features:
 * - Shows loading state while checking authentication
 * - Redirects to /login if not authenticated
 * - Preserves intended destination in URL params for redirect after login
 * - Only renders children if user is authenticated
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/auth'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * ProtectedRoute - Route wrapper that requires authentication
 * 
 * @param children - The component to render if authenticated
 * @returns Either the protected component or a redirect to login
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const location = useLocation()
  
  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen app-shell">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--rq-text)] mx-auto mb-4"></div>
          <p className="text-[var(--rq-text-soft)]">Loading...</p>
        </div>
      </div>
    )
  }
  
  // If not authenticated, redirect to login with return URL
  if (!user) {
    // Preserve the intended destination so we can redirect back after login
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }
  
  // User is authenticated, render the protected component
  return <>{children}</>
}
